import {
  Button,
  DialogRoot,
  DialogTrigger,
  Item,
  ItemActions,
  ItemDescription,
  ItemHeader,
  ItemTitle,
} from '@ibsys/kit';
import { XCircleIcon } from 'lucide-react';
import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useAddEmployeesToGroup,
  useEmployeeGroup,
  useRemoveEmployeesFromGroup,
} from '@/entities/employeeGroups';
import type { EmployeeListItem } from '@/entities/conversations/model/api/types';
import { getFullName } from '@/shared/lib';
import { AddOperatorsDialog } from './AddOperatorsDialog';

type Props = {
  groupId: number;
  disabled?: boolean;
};

/**
 * Список операторов в группе с возможностью добавления и удаления.
 * Данные приходят из GET /employeeGroup/{id}; мутации инвалидируют detail-кеш группы.
 */
export const EmployeesList: FC<Props> = ({ groupId, disabled }) => {
  const { t } = useTranslation(['common', 'employees']);
  const { data } = useEmployeeGroup(groupId);
  const { mutateAsync: addEmployees, isPending: isAdding } = useAddEmployeesToGroup();
  const { mutateAsync: removeEmployees, isPending: isRemoving } = useRemoveEmployeesFromGroup();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  const employees = data?.employees ?? [];

  const handleSaveDialog = async (selected: EmployeeListItem[]) => {
    if (selected.length === 0) {
      setIsDialogOpen(false);
      return;
    }
    await addEmployees({ id: groupId, body: { employeeIds: selected.map((employee) => employee.id) } });
    setIsDialogOpen(false);
  };

  const handleRemoveEmployee = async (employeeId: number) => {
    await removeEmployees({ id: groupId, body: { employeeIds: [employeeId] } });
  };

  const formatFio = (employee: { lastName?: string | null; firstName?: string | null; middleName?: string | null }) =>
    getFullName(employee.firstName, employee.lastName, employee.middleName);

  return (
    <DialogRoot open={isDialogOpen} onOpenChange={setIsDialogOpen}>
      <div className="flex flex-col gap-4">
        <div className="flex gap-5 items-center">
          <p className="font-medium">{t('employees:groups.operators')}</p>
          {!disabled && (
            <DialogTrigger asChild>
              <Button variant="secondary" size="sm" className="ml-auto" isLoading={isAdding}>
                {t('employees:groups.addOperator')}
              </Button>
            </DialogTrigger>
          )}
        </div>

        <div className="flex flex-col gap-3">
          {employees.map((employee) => (
            <Item variant="outline" size="sm" key={employee.id}>
              <ItemHeader>
                <ItemTitle>{formatFio(employee)}</ItemTitle>
                <ItemActions className="ml-4">
                  {!disabled && (
                    <Button
                      icon={<XCircleIcon />}
                      variant="ghost"
                      color="destructive"
                      isLoading={isRemoving}
                      aria-label={t('common:delete')}
                      onClick={() => handleRemoveEmployee(employee.id)}
                    />
                  )}
                </ItemActions>
              </ItemHeader>
              {employee.externalId && (
                <ItemDescription className="text-sm text-muted-foreground line-clamp-1">
                  {t('employees:externalId')}: {employee.externalId}
                </ItemDescription>
              )}
            </Item>
          ))}
          {employees.length === 0 && (
            <p className="text-sm text-muted-foreground">{t('employees:groups.noOperatorsInGroup')}</p>
          )}
        </div>
      </div>

      <AddOperatorsDialog
        existingEmployeeIds={employees.map((employee) => employee.id)}
        onCancel={() => setIsDialogOpen(false)}
        onSave={handleSaveDialog}
        className="max-w-160"
      />
    </DialogRoot>
  );
};
